import * as THREE from 'three';
import { EntityType, type ResourceTier, type RaceId, type ShipClass, type NPCDisposition, RESOURCE_INFO, RACE_INFO, SHIP_CLASS_KEY, SHIP_CLASS_VISUAL_SCALE, DEFAULT_SHIP_COSMETICS, DEFAULT_SHIP_PRIMARY_COLOR, type PlayerShipCosmetics, type ShipPatternId } from '@viral-space/shared';
import type { GameScene } from '../engine/scene';
import type { AssetLoader } from '../engine/loaders';
import type { ClientEntity } from './state';
import type { GameState } from './state';
import { buildShipMesh, type ShipClassId } from './ship-builder';

/**
 * Pull the per-pilot ship cosmetics (pattern + accent color) off an
 * entity's `_extra` bag if the server has stamped them. Only player
 * ship entities carry this — NPCs render with their race / faction
 * colors, not customizable patterns.
 *
 * Hull (primary) color is intentionally NOT in this bundle: it is
 * derived from the pilot's race in {@link createShipVisual} so all
 * Terran ships always read as Terran in PvP, etc. Pilots customize
 * only the accent color and the pattern.
 *
 * Falls back to {@link DEFAULT_SHIP_COSMETICS} when the field is
 * missing or malformed so a malformed broadcast can never throw the
 * renderer; the worst case is a brief render with the default solid
 * hull until the next valid update.
 */
function readShipCosmetics(extra: Record<string, unknown>): PlayerShipCosmetics {
  const raw = extra['shipCosmetics'];
  if (!raw || typeof raw !== 'object') return DEFAULT_SHIP_COSMETICS;
  const r = raw as Partial<PlayerShipCosmetics>;
  return {
    pattern: (typeof r.pattern === 'string' ? r.pattern : DEFAULT_SHIP_COSMETICS.pattern) as ShipPatternId,
    secondaryColor: typeof r.secondaryColor === 'number' ? r.secondaryColor : DEFAULT_SHIP_COSMETICS.secondaryColor,
  };
}

function cosmeticsKey(c: PlayerShipCosmetics): string {
  return `${c.pattern}|${c.secondaryColor.toString(16)}`;
}

/**
 * Resolve the hull (primary) plate color for a player ship from the
 * pilot's race. Hull color is fixed per race so civilizations stay
 * visually distinct in PvP — players customize the accent, never the
 * hull. Falls back to {@link DEFAULT_SHIP_PRIMARY_COLOR} when the
 * race id is missing or unknown (legacy entity, race row deleted).
 */
function resolveShipPrimaryColor(extra: Record<string, unknown>): number {
  const raceId = extra['raceId'];
  if (typeof raceId === 'number') {
    const info = (RACE_INFO as Record<number, { primaryColor?: number }>)[raceId];
    if (info && typeof info.primaryColor === 'number') return info.primaryColor;
  }
  return DEFAULT_SHIP_PRIMARY_COLOR;
}

/**
 * Normalise the wire-format `shipClass` field into a string slug that
 * `buildShipMesh`'s `BUILDERS` map understands.
 *
 * The protocol is asymmetric: NPCs send `extra.shipClass` as the
 * lowercase slug (`'fighter'`, `'cruiser'`, …) because their server-
 * side `Entity.resource` field is already a slug, while player ships
 * send `extra.shipClass` as the **numeric** `ShipClass` enum value
 * (set by `ConnectionManager.spawnPlayerWithState`). Both flow through
 * the same `compact.extra.shipClass` slot on the wire, so the renderer
 * has to handle either form.
 *
 * Without this conversion `BUILDERS[3]` is `undefined`, the builder
 * silently falls through to `BUILDERS.fighter`, and every player
 * Cruiser/Destroyer/Mothership renders as a Fighter — which is the
 * exact bug this helper was added to fix. Keeping the asymmetry
 * documented here (rather than forcing one form on the wire) avoids
 * a flag-day protocol change while still rendering the correct hull.
 */
function resolveShipClassSlug(raw: ShipClass | string | undefined): ShipClassId {
  if (typeof raw === 'string' && raw.length > 0) return raw as ShipClassId;
  if (typeof raw === 'number') {
    const slug = (SHIP_CLASS_KEY as Record<number, string>)[raw];
    if (slug) return slug as ShipClassId;
  }
  return 'fighter';
}

interface EntityVisual {
  group: THREE.Group;
  entityType: EntityType;
  /**
   * Resolved ship-class slug the visual was built for. Set on Ship and
   * NPC visuals so `update()` can detect a mid-life hull swap (player
   * switches ships at the hangar, NPC template change) and rebuild the
   * mesh instead of leaving the old geometry on screen forever. Empty
   * for non-ship entity types.
   */
  shipClassSlug?: string;
  /**
   * Hash of the per-pilot ship cosmetics bundle (pattern + colors)
   * the visual was built with. Set on player ship visuals so
   * `update()` can rebuild the mesh when the pilot equips a new
   * pattern or color from the Hangar Customize panel.
   */
  cosmeticsKey?: string;
  trail?: THREE.Points;
  atmosphere?: THREE.Mesh;
  glowSprite?: THREE.Sprite;
  wireframe?: THREE.LineSegments;
  particleTail?: THREE.Points;
  spinnable?: THREE.Mesh;
  shieldBubble?: THREE.Mesh;
  shieldBubbleStyle?: string;
  shieldBubbleColor?: number;
  shieldFlash?: number;
  weaponMount?: THREE.Group;
  weaponMountStyle?: string;
  /**
   * Thruster core materials whose `emissiveIntensity` is animated each
   * frame to make engines "breathe". One entry per nozzle (motherships
   * have many, scouts have one). Empty for entities without engines.
   */
  thrusterCores?: THREE.MeshStandardMaterial[];
  /**
   * Random per-instance phase offset so two adjacent ships don't pulse
   * in lockstep (which would look mechanical).
   */
  pulsePhase?: number;
  /**
   * Materials for "beacon" lights that pulse slowly to make the entity
   * easier to spot in deep space — used by stations (docking bay
   * indicators) and warp gates (rim accents). Animated in `updateVisual`
   * via the same per-instance phase offset as thrusters so two nearby
   * stations don't blink in sync. Each entry is the *emissive* material
   * shared across that entity's beacon meshes; we re-tint it every
   * frame instead of swapping geometries.
   */
  beaconMaterials?: THREE.MeshStandardMaterial[];
  /**
   * Optional dynamic light source attached to the visual group. Used
   * by stations and warp gates so they cast actual illumination on
   * nearby asteroids / ships, not just self-glow. We keep a reference
   * here so the update tick can pulse the light's intensity in lockstep
   * with the beacon materials and the dispose path can release it.
   */
  pointLight?: THREE.PointLight;
}

const RESOURCE_COLORS: Record<string, number> = {
  iron: 0x8b7355,
  copper: 0xb87333,
  titanium: 0xc0c0c0,
  gold: 0xffd700,
  crystal: 0x00ffff,
  dark_matter: 0x6600cc,
  plasma: 0xff4400,
  quantum: 0xff00ff,
};

const DISPOSITION_COLORS: Record<string, number> = {
  hostile: 0xff2222,
  friendly: 0x22ff44,
  neutral: 0xffff22,
};

/**
 * Visual radius multiplier per ship class. Single source of truth lives
 * in `@viral-space/shared` (`SHIP_CLASS_VISUAL_SCALE`) so the server can
 * derive matching projectile hit radii — keeping "what you see" exactly
 * equal to "what you hit" on wings, engines, and noses alike.
 */
const SHIP_CLASS_SCALES = SHIP_CLASS_VISUAL_SCALE;

export class EntityRenderer {
  private visuals = new Map<string, EntityVisual>();
  private scene: GameScene;
  private assets: AssetLoader;
  private clock = new THREE.Clock();
  /**
   * Counter that increments every time a brand-new EntityVisual is
   * constructed. Read by `ViralSpace.loop` to drive a one-frame
   * shader pre-compile pass — without this, every newly-broadcast
   * entity (NPC, projectile, ResourcePatch) compiles its shader on
   * first render, which the user sees as a single-frame freeze.
   * The compile pass walks the whole scene anyway and skips
   * already-compiled materials, so a stale read here is harmless.
   */
  private visualsCreatedSinceLastPoll = 0;

  constructor(scene: GameScene, assets: AssetLoader) {
    this.scene = scene;
    this.assets = assets;
  }

  /**
   * Returns and clears the count of newly-created entity visuals
   * since the last call. Used by the main loop to schedule a shader
   * pre-compile pass on the frame after new entities arrive — that
   * pass is what eliminates the "freezes when I move" stutter
   * caused by Three.js's lazy first-render shader compilation as
   * new NPCs / projectiles enter the camera frustum.
   */
  consumeVisualsCreatedCount(): number {
    const n = this.visualsCreatedSinceLastPoll;
    this.visualsCreatedSinceLastPoll = 0;
    return n;
  }

  update(gameState: GameState): void {
    const dt = this.clock.getDelta();
    const entities = gameState.getEntities();

    const activeIds = new Set<string>();
    for (const [id, entity] of entities) {
      activeIds.add(id);
      let visual = this.visuals.get(id);
      // Detect a mid-life hull swap (player switches ships at the
      // hangar, or an NPC template gets reassigned) and rebuild the
      // visual from scratch — otherwise the cached EntityVisual keeps
      // the old `buildShipMesh` group on screen forever, which is what
      // the user reported as "the ship doesn't change after switching".
      //
      // Surface tanks are excluded: their visual is built by
      // `createSurfaceTankVisual` with `shipClassSlug='surface-tank'`
      // while the wire still carries the underlying flying-ship class
      // ('fighter', 'cruiser', …). Without this guard the slug check
      // tripped every frame and we tore down + rebuilt 8 hover tanks
      // each frame on the surface — which is exactly what the user
      // reported as "game freezes when I move".
      if (
        visual &&
        (entity.type === EntityType.Ship || entity.type === EntityType.NPC) &&
        entity.extra['surfaceNpc'] !== true
      ) {
        const desired = resolveShipClassSlug(entity.shipClass);
        if (visual.shipClassSlug && visual.shipClassSlug !== desired) {
          this.scene.removeEntity(id);
          this.disposeVisual(visual);
          this.visuals.delete(id);
          visual = undefined;
        }
      }
      // Player ships also rebuild when the per-pilot cosmetics
      // change (Hangar Customize panel apply, premium-pattern
      // un-clamp on resub, etc). Pattern + colors live in
      // `_extra.shipCosmetics` and are stamped server-side; comparing
      // a stable hash means we only rebuild when the bundle actually
      // differs.
      if (visual && entity.type === EntityType.Ship && visual.cosmeticsKey) {
        const desiredCosmetics = readShipCosmetics(entity.extra);
        const desiredKey = cosmeticsKey(desiredCosmetics);
        if (desiredKey !== visual.cosmeticsKey) {
          this.scene.removeEntity(id);
          this.disposeVisual(visual);
          this.visuals.delete(id);
          visual = undefined;
        }
      }
      if (!visual) {
        visual = this.createVisual(entity);
        this.visuals.set(id, visual);
        this.scene.addEntity(id, visual.group);
        this.visualsCreatedSinceLastPoll++;
      }
      this.updateVisual(visual, entity, dt);
    }

    for (const [id, visual] of this.visuals) {
      if (!activeIds.has(id)) {
        this.scene.removeEntity(id);
        this.disposeVisual(visual);
        this.visuals.delete(id);
      }
    }
  }

  private createVisual(entity: ClientEntity): EntityVisual {
    let visual: EntityVisual;
    // Surface bases and surface NPCs hijack the regular Station / NPC
    // dispatchers and use ground-vehicle / building geometry instead
    // of orbital station + flying-ship meshes. Detected via the
    // `_extra.surfaceBase` / `_extra.surfaceNpc` flags the server
    // stamps in {@link spawnSurfaceBase} / {@link spawnSurfaceNpc}.
    if (entity.type === EntityType.Station && entity.extra['surfaceBase']) {
      visual = this.createSurfaceBaseVisual(entity);
    } else if (entity.type === EntityType.NPC && entity.extra['surfaceNpc']) {
      visual = this.createSurfaceTankVisual(entity);
    } else {
      switch (entity.type) {
        case EntityType.Ship: visual = this.createShipVisual(entity); break;
        case EntityType.Asteroid: visual = this.createAsteroidVisual(entity); break;
        case EntityType.Projectile: visual = this.createProjectileVisual(entity); break;
        case EntityType.Planet: visual = this.createPlanetVisual(entity); break;
        case EntityType.Star: visual = this.createStarVisual(entity); break;
        case EntityType.Station: visual = this.createStationVisual(entity); break;
        case EntityType.WarpGate: visual = this.createWarpGateVisual(entity); break;
        case EntityType.Comet: visual = this.createCometVisual(entity); break;
        case EntityType.ResourceDrop: visual = this.createResourceDropVisual(entity); break;
        case EntityType.NPC: visual = this.createNPCVisual(entity); break;
        default: visual = this.createDefaultVisual(entity); break;
      }
    }
    if (EntityRenderer.FOG_IMMUNE_TYPES.has(entity.type)) {
      this.disableFogOnGroup(visual.group);
    }
    return visual;
  }

  private createShipVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();
    const shipClass = resolveShipClassSlug(entity.shipClass);
    const scale = SHIP_CLASS_SCALES[shipClass] ?? 2.5;

    // Per-pilot cosmetics — pattern + accent color only. Hull
    // color is derived from the pilot's race below so different
    // civilizations always read as themselves (no Terran-painted
    // Krelg cruisers in PvP). When the server hasn't pushed
    // cosmetics yet — common on the very first frame after a
    // hangar swap — we fall back to DEFAULT_SHIP_COSMETICS, which
    // is calibrated to match the pre-cosmetics cyan accent so
    // existing pilots see no visual change.
    const cosmetics = readShipCosmetics(entity.extra);
    const primaryColor = resolveShipPrimaryColor(entity.extra);
    const built = buildShipMesh(shipClass, {
      primaryColor,
      secondaryColor: cosmetics.secondaryColor,
      thrusterColor: 0x66ccff,
      cockpitColor: cosmetics.secondaryColor,
      emissiveBoost: 0.28,
      pattern: cosmetics.pattern,
    });
    built.group.scale.setScalar(scale);
    group.add(built.group);

    const trail = this.createEngineTrail(0x44ccff, 80, 0.2);
    trail.position.set(0, 0, scale * 1.2);
    group.add(trail);

    const glowMat = new THREE.SpriteMaterial({
      map: createGlowTexture(),
      color: 0x2299ff,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.3,
    });
    const glowSprite = new THREE.Sprite(glowMat);
    glowSprite.scale.setScalar(scale * 4);
    group.add(glowSprite);

    return {
      group,
      entityType: entity.type,
      shipClassSlug: shipClass,
      cosmeticsKey: cosmeticsKey(cosmetics),
      trail,
      glowSprite,
      thrusterCores: built.thrusterCores,
      pulsePhase: Math.random() * Math.PI * 2,
    };
  }

  private createNPCVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();

    // Surface fauna (Phase 3.1) — primitive low-poly meshes, no
    // ship-class build. The `_extra.faunaKind` value selects the
    // mesh; we keep the standard NPC `group.position` interpolation
    // path by returning the same `EntityVisual` shape.
    if (entity.extra['fauna'] === true) {
      const kind = (entity.extra['faunaKind'] as number) ?? 0;
      this.buildSurfaceFauna(group, kind);
      return {
        group,
        entityType: entity.type,
        shipClassSlug: 'fauna',
        thrusterCores: [],
        pulsePhase: Math.random() * Math.PI * 2,
      };
    }

    const shipClass = resolveShipClassSlug(entity.shipClass);
    const baseScale = SHIP_CLASS_SCALES[shipClass] ?? 2.5;
    const npcLevel = (entity.extra['npcLevel'] as number) ?? 1;
    // Motherships scale less aggressively with level — they're already
    // huge — to avoid screen-eating at level 30+.
    const levelMul = shipClass === 'mothership'
      ? 1 + Math.min(npcLevel - 1, 40) * 0.02
      : 1 + Math.min(npcLevel - 1, 20) * 0.04;
    const scale = baseScale * levelMul;

    const raceId = entity.extra['raceId'] as number | undefined;
    const raceInfo = raceId !== undefined
      ? (RACE_INFO as Record<number, { primaryColor: number; secondaryColor: number }>)[raceId]
      : undefined;

    const fallbackPrimary = DISPOSITION_COLORS[entity.disposition ?? 'neutral'] ?? 0xffff22;
    const primary = raceInfo?.primaryColor ?? fallbackPrimary;
    const secondary = raceInfo?.secondaryColor ?? primary;

    const emissiveBoost = 0.3 + Math.min(npcLevel, 20) * 0.04;

    const built = buildShipMesh(shipClass, {
      primaryColor: primary,
      secondaryColor: secondary,
      thrusterColor: secondary,
      cockpitColor: secondary,
      emissiveBoost,
    });
    built.group.scale.setScalar(scale);
    group.add(built.group);

    const trailLen = shipClass === 'mothership' ? 140 : 80;
    const trailSize = shipClass === 'mothership' ? 0.5 : 0.2;
    const trail = this.createEngineTrail(secondary, trailLen, trailSize);
    trail.position.set(0, 0, scale * 1.2);
    group.add(trail);

    let glowSprite: THREE.Sprite | undefined;
    if (npcLevel >= 3 || shipClass === 'mothership') {
      const auraColor = shipClass === 'mothership' ? primary : secondary;
      const opacity = shipClass === 'mothership'
        ? 0.5
        : Math.min(0.15 + npcLevel * 0.02, 0.5);
      const auraScale = shipClass === 'mothership' ? scale * 5 : scale * 3.5;
      const glowMat = new THREE.SpriteMaterial({
        map: createGlowTexture(),
        color: auraColor,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        opacity,
      });
      glowSprite = new THREE.Sprite(glowMat);
      glowSprite.scale.setScalar(auraScale);
      group.add(glowSprite);
    }

    return {
      group,
      entityType: entity.type,
      shipClassSlug: shipClass,
      trail,
      glowSprite,
      thrusterCores: built.thrusterCores,
      pulsePhase: Math.random() * Math.PI * 2,
    };
  }

  private createAsteroidVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();

    const tier = entity.resourceTier as string | undefined;
    const resourceInfo = tier !== undefined
      ? (RESOURCE_INFO as Record<string, { color?: number }>)[tier]
      : undefined;
    const color = resourceInfo?.color ?? RESOURCE_COLORS[tier ?? ''] ?? 0x888888;

    // Planet-side mineral nodes — the server tags surface resource
    // patches with `_extra.surfaceMineral` so the client can swap
    // the orbital pitted-icosahedron asteroid for a crystalline
    // outcrop that reads as a ground mineral instead of a floating
    // rock. Resource tier still drives the colour so the player
    // can recognise an Iron deposit vs a Crystal deposit at a
    // glance, but the silhouette is intentionally different.
    if (entity.extra['surfaceMineral'] === true) {
      this.buildSurfaceMineralCluster(group, color);
      return { group, entityType: entity.type };
    }

    // Discoverable surface ruins (Phase 2.1). Server stamps
    // `_extra.ruin = true` + `_extra.ruinKind` so the client picks
    // one of three low-poly silhouettes (Crash / Precursor / Colony).
    // Ruins share the Asteroid entity slot for serialisation /
    // spatial-hash purposes only; gameplay-wise they're inert
    // (huge hp), and the world-label uses the server-supplied
    // `_extra.name`.
    if (entity.extra['ruin'] === true) {
      const kind = (entity.extra['ruinKind'] as number | undefined) ?? 0;
      this.buildSurfaceRuin(group, kind);
      return { group, entityType: entity.type };
    }

    const size = 4 + Math.random() * 4;
    const geometry = new THREE.IcosahedronGeometry(size, 1);
    randomizeVertices(geometry, 0.4);
    const material = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.15,
      metalness: 0.5,
      roughness: 0.7,
      flatShading: true,
    });
    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);

    const wireGeo = new THREE.WireframeGeometry(geometry);
    const wireMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.2,
    });
    const wireframe = new THREE.LineSegments(wireGeo, wireMat);
    group.add(wireframe);

    return { group, entityType: entity.type, wireframe };
  }

  /**
   * Build a planet-side mineral outcrop: a small rocky base with
   * 3–5 angular crystal shards growing out of it at varied angles.
   * Visually unmistakable from the orbital asteroid mesh — sharp
   * silhouette, glowing core, no spinning wireframe overlay (the
   * wireframe pattern reads as "loose rock in zero-g" on the
   * orbital mesh, which is exactly the wrong vibe for a static
   * deposit cemented to the ground).
   *
   * Mesh count is intentionally low (≤ 6 small primitives per
   * cluster) so spawning a dense Refinery patch (16 nodes) only
   * adds ≈ 100 draw items — well within the renderer's budget.
   */
  private buildSurfaceMineralCluster(group: THREE.Group, color: number): void {
    const baseColor = new THREE.Color(color);
    const darkRock = baseColor.clone().lerp(new THREE.Color(0x222222), 0.7);
    // Rocky base — a flat-ish dodecahedron sitting on the ground so
    // the cluster reads as anchored to the terrain instead of
    // floating like an orbital asteroid.
    const baseGeo = new THREE.DodecahedronGeometry(2.4, 0);
    randomizeVertices(baseGeo, 0.35);
    const baseMat = new THREE.MeshStandardMaterial({
      color: darkRock,
      metalness: 0.3,
      roughness: 0.95,
      flatShading: true,
    });
    const baseMesh = new THREE.Mesh(baseGeo, baseMat);
    baseMesh.scale.set(1.2, 0.7, 1.2);
    baseMesh.position.y = 0.6;
    group.add(baseMesh);

    // Crystal shards — 3–5 octahedrons of varying height growing
    // outward at random angles. We seed Math.random per visual so
    // each deposit looks slightly different, but the deterministic
    // server position already gives nearby nodes a consistent
    // "this is a patch" silhouette.
    const shardMat = new THREE.MeshStandardMaterial({
      color: baseColor,
      emissive: baseColor,
      emissiveIntensity: 0.35,
      metalness: 0.55,
      roughness: 0.35,
      flatShading: true,
      transparent: true,
      opacity: 0.95,
    });
    const shardCount = 3 + Math.floor(Math.random() * 3);
    for (let i = 0; i < shardCount; i++) {
      const height = 2.6 + Math.random() * 3.4;
      const radius = 0.6 + Math.random() * 0.7;
      const shardGeo = new THREE.OctahedronGeometry(radius, 0);
      shardGeo.scale(1, height / radius, 1);
      const shard = new THREE.Mesh(shardGeo, shardMat);
      // Pose: lean each shard a few degrees off vertical and offset
      // around the base so they read as a cluster.
      const a = (i / shardCount) * Math.PI * 2 + Math.random() * 0.4;
      const r = 0.9 + Math.random() * 0.6;
      shard.position.set(
        Math.cos(a) * r,
        height * 0.35 + 0.6,
        Math.sin(a) * r,
      );
      shard.rotation.set(
        (Math.random() - 0.5) * 0.6,
        Math.random() * Math.PI,
        (Math.random() - 0.5) * 0.6,
      );
      group.add(shard);
    }

    // Single bright core shard centred above the base — gives the
    // deposit a clear focal point and reads at distance through
    // dust / atmosphere.
    const coreGeo = new THREE.OctahedronGeometry(1.4, 0);
    coreGeo.scale(0.9, 1.8, 0.9);
    const coreMat = new THREE.MeshBasicMaterial({
      color: baseColor.clone().lerp(new THREE.Color(0xffffff), 0.5),
      transparent: true,
      opacity: 0.95,
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    core.position.y = 2.8;
    group.add(core);
  }

  /**
   * Build a low-poly surface ruin silhouette keyed off the
   * `_extra.ruinKind` numeric enum (matches shared `RuinKind`).
   *
   * Mesh budget is kept low (≤ 8 primitives per ruin) — surfaces
   * spawn 1–4 ruins each, so per-surface ruin cost is well inside
   * the renderer's adaptive-quality envelope.
   *
   * The three kinds intentionally read very differently at
   * distance so the pilot can identify a Crash / Precursor /
   * Colony site without having to fly close enough to trigger
   * the world-label.
   */
  private buildSurfaceRuin(group: THREE.Group, kind: number): void {
    // 0 = Crash, 1 = Precursor, 2 = Colony — keep in sync with
    // shared `RuinKind`. Default to Crash for forward-compat.
    if (kind === 1) {
      // Precursor obelisks — stacked alien monoliths with glowing
      // runes. Tall + thin silhouette, bright cyan emissive accent.
      const stoneMat = new THREE.MeshStandardMaterial({
        color: 0x4a4f5e, metalness: 0.4, roughness: 0.85, flatShading: true,
      });
      const runeMat = new THREE.MeshBasicMaterial({
        color: 0x44ccff, transparent: true, opacity: 0.85,
      });
      const baseGeo = new THREE.CylinderGeometry(4, 4.5, 2, 6);
      const base = new THREE.Mesh(baseGeo, stoneMat);
      base.position.y = 1;
      group.add(base);
      const tallGeo = new THREE.BoxGeometry(1.5, 9, 1.5);
      const tall = new THREE.Mesh(tallGeo, stoneMat);
      tall.position.y = 6.5;
      group.add(tall);
      const midGeo = new THREE.BoxGeometry(2.4, 1.2, 2.4);
      const mid = new THREE.Mesh(midGeo, stoneMat);
      mid.position.y = 11.7;
      group.add(mid);
      // Two glowing rune rings.
      const ringGeo = new THREE.TorusGeometry(0.95, 0.15, 6, 18);
      const ring1 = new THREE.Mesh(ringGeo, runeMat);
      ring1.rotation.x = Math.PI / 2;
      ring1.position.y = 4;
      group.add(ring1);
      const ring2 = new THREE.Mesh(ringGeo, runeMat);
      ring2.rotation.x = Math.PI / 2;
      ring2.position.y = 8.5;
      group.add(ring2);
    } else if (kind === 2) {
      // Colony ruin — collapsed prefab walls + a couple of storage
      // crates. Wider footprint, warm orange accents.
      const prefabMat = new THREE.MeshStandardMaterial({
        color: 0x8a7660, metalness: 0.2, roughness: 0.9, flatShading: true,
      });
      const crateMat = new THREE.MeshStandardMaterial({
        color: 0xcc8844, metalness: 0.4, roughness: 0.7, flatShading: true,
      });
      const slab = new THREE.Mesh(
        new THREE.BoxGeometry(9, 0.6, 7), prefabMat,
      );
      slab.position.y = 0.3;
      group.add(slab);
      const wallA = new THREE.Mesh(
        new THREE.BoxGeometry(0.4, 3.5, 6), prefabMat,
      );
      wallA.position.set(-4.4, 2, -0.5);
      wallA.rotation.z = -0.18;
      group.add(wallA);
      const wallB = new THREE.Mesh(
        new THREE.BoxGeometry(0.4, 2.2, 4), prefabMat,
      );
      wallB.position.set(2.5, 1.3, 2.5);
      wallB.rotation.x = 0.15;
      group.add(wallB);
      const crate1 = new THREE.Mesh(
        new THREE.BoxGeometry(1.5, 1.5, 1.5), crateMat,
      );
      crate1.position.set(-1.2, 1.0, 1.8);
      group.add(crate1);
      const crate2 = new THREE.Mesh(
        new THREE.BoxGeometry(1.8, 1.0, 1.4), crateMat,
      );
      crate2.position.set(1.6, 0.7, -1.6);
      crate2.rotation.y = 0.4;
      group.add(crate2);
    } else {
      // Crash site — half-buried hull plates, scorch decals, broken
      // wing piece. Dark metal silhouette with glowing fracture
      // lines. The default branch so older blueprints that never
      // get a `ruinKind` still render a meaningful shape.
      const hullMat = new THREE.MeshStandardMaterial({
        color: 0x3a3a3a, metalness: 0.7, roughness: 0.55, flatShading: true,
      });
      const scorchMat = new THREE.MeshBasicMaterial({
        color: 0xff6622, transparent: true, opacity: 0.65,
      });
      // Main hull chunk — half-buried so it leans at an angle.
      const hull = new THREE.Mesh(
        new THREE.BoxGeometry(7, 2.5, 3.5), hullMat,
      );
      hull.position.y = 0.8;
      hull.rotation.z = -0.18;
      hull.rotation.x = 0.12;
      group.add(hull);
      // Broken wing.
      const wing = new THREE.Mesh(
        new THREE.BoxGeometry(5, 0.4, 1.6), hullMat,
      );
      wing.position.set(-3.5, 1.4, 1.6);
      wing.rotation.z = -0.45;
      group.add(wing);
      // Fragment shards.
      const shard = new THREE.Mesh(
        new THREE.BoxGeometry(1.8, 0.3, 1.0), hullMat,
      );
      shard.position.set(3.5, 0.2, -1.6);
      shard.rotation.y = 0.8;
      group.add(shard);
      // Scorch sphere for emissive accent.
      const scorch = new THREE.Mesh(
        new THREE.SphereGeometry(0.7, 8, 8), scorchMat,
      );
      scorch.position.set(0.5, 1.6, 0);
      group.add(scorch);
    }
  }

  /**
   * Build a low-poly mesh for one of the surface fauna kinds
   * (Phase 3.1). Kept tiny (≤ 6 primitives per fauna) — surfaces
   * spawn up to 8 of these, so per-surface mesh cost stays well
   * inside the renderer's adaptive budget. Each kind reads
   * distinctly at distance so the pilot can pick combat tactics
   * without having to fly close enough to trigger the label.
   *
   * Kind enum: 0 RockGrub, 1 LavaWyrm, 2 IceSerpent, 3 BiofluxStrider,
   * 4 DustReaver. Default falls back to a generic grub shape.
   */
  private buildSurfaceFauna(group: THREE.Group, kind: number): void {
    if (kind === 1) {
      // LavaWyrm — segmented worm coiled out of the ground. Hot
      // orange emissive body.
      const bodyMat = new THREE.MeshStandardMaterial({
        color: 0x661811, emissive: 0xcc4422, emissiveIntensity: 0.8,
        metalness: 0.3, roughness: 0.7, flatShading: true,
      });
      const headMat = new THREE.MeshStandardMaterial({
        color: 0xff6622, emissive: 0xff9944, emissiveIntensity: 1.0,
        metalness: 0.2, roughness: 0.6, flatShading: true,
      });
      for (let i = 0; i < 4; i++) {
        const seg = new THREE.Mesh(
          new THREE.SphereGeometry(1.4 - i * 0.15, 8, 6), bodyMat,
        );
        seg.position.set(i * 1.4, 1.0 + Math.sin(i) * 0.4, 0);
        group.add(seg);
      }
      const head = new THREE.Mesh(
        new THREE.ConeGeometry(0.8, 1.6, 6), headMat,
      );
      head.position.set(-0.9, 1.4, 0);
      head.rotation.z = Math.PI / 2;
      group.add(head);
    } else if (kind === 2) {
      // IceSerpent — slim crystal-blue snake with a single tall fin.
      const scaleMat = new THREE.MeshStandardMaterial({
        color: 0xa8d8ff, emissive: 0x88aaff, emissiveIntensity: 0.4,
        metalness: 0.5, roughness: 0.4, flatShading: true,
      });
      const fin = new THREE.Mesh(
        new THREE.ConeGeometry(0.5, 1.6, 4), scaleMat,
      );
      fin.position.set(0, 2.2, 0);
      group.add(fin);
      for (let i = 0; i < 5; i++) {
        const seg = new THREE.Mesh(
          new THREE.SphereGeometry(0.7 - i * 0.05, 8, 6), scaleMat,
        );
        seg.position.set(i * 0.9 - 1.6, 0.9, 0);
        group.add(seg);
      }
    } else if (kind === 3) {
      // BiofluxStrider — long-legged grass-green creature, lithe.
      const bodyMat = new THREE.MeshStandardMaterial({
        color: 0x3a8a5a, emissive: 0x66cc88, emissiveIntensity: 0.3,
        metalness: 0.2, roughness: 0.7, flatShading: true,
      });
      const torso = new THREE.Mesh(
        new THREE.SphereGeometry(1.2, 8, 6), bodyMat,
      );
      torso.scale.set(1.4, 0.8, 0.8);
      torso.position.y = 2.4;
      group.add(torso);
      for (let i = 0; i < 4; i++) {
        const leg = new THREE.Mesh(
          new THREE.CylinderGeometry(0.15, 0.15, 2.4, 5), bodyMat,
        );
        leg.position.set(((i % 2) - 0.5) * 1.4, 1.2, (Math.floor(i / 2) - 0.5) * 1.4);
        group.add(leg);
      }
    } else if (kind === 4) {
      // DustReaver — squat tank-like creature, sand-tan, armored.
      const armorMat = new THREE.MeshStandardMaterial({
        color: 0xa07840, metalness: 0.4, roughness: 0.7, flatShading: true,
      });
      const spineMat = new THREE.MeshStandardMaterial({
        color: 0x402814, metalness: 0.6, roughness: 0.5, flatShading: true,
      });
      const body = new THREE.Mesh(
        new THREE.SphereGeometry(2.2, 8, 6), armorMat,
      );
      body.scale.set(1.6, 0.9, 1.2);
      body.position.y = 1.8;
      group.add(body);
      for (let i = 0; i < 3; i++) {
        const spike = new THREE.Mesh(
          new THREE.ConeGeometry(0.35, 1.2, 4), spineMat,
        );
        spike.position.set((i - 1) * 0.9, 3.1, 0);
        group.add(spike);
      }
    } else {
      // RockGrub (default) — squat blue-grey blob with a stubby head.
      const bodyMat = new THREE.MeshStandardMaterial({
        color: 0x5a6470, metalness: 0.3, roughness: 0.8, flatShading: true,
      });
      const eyeMat = new THREE.MeshBasicMaterial({ color: 0xffeeaa });
      const body = new THREE.Mesh(
        new THREE.SphereGeometry(1.2, 8, 6), bodyMat,
      );
      body.scale.set(1.4, 0.6, 1.0);
      body.position.y = 0.8;
      group.add(body);
      const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.7, 6, 5), bodyMat,
      );
      head.position.set(1.0, 1.0, 0);
      group.add(head);
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.15, 5, 4), eyeMat,
      );
      eye.position.set(1.4, 1.15, 0.3);
      group.add(eye);
    }
  }

  private createProjectileVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();

    const color = (entity.extra['projectileColor'] as number) ?? 0xff4400;
    const size = (entity.extra['projectileSize'] as number) ?? 0.15;
    const style = (entity.extra['visualStyle'] as string) ?? 'blaster';
    const trailStyle = (entity.extra['trailStyle'] as string) ?? 'basic';

    const baseRadius = Math.max(0.05, Math.min(0.6, size));

    // ── Basic default blaster: small sphere + subtle glow + short trail.
    //    This intentionally matches the first-version look so the free
    //    fallback weapon always reads as humble compared to crafted/bought
    //    options.
    if (style === 'blaster') {
      const core = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius, 8, 8),
        new THREE.MeshBasicMaterial({ color }),
      );
      group.add(core);
      const glow = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 2, 8, 8),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.4 }),
      );
      group.add(glow);
      const trail = this.createEngineTrail(color, 30, baseRadius * 0.55);
      trail.position.set(0, 0, -baseRadius * 1.3);
      group.add(trail);
      return { group, entityType: entity.type, trail };
    }

    // ── Beam / laser / rail: bright tapered beam with white-hot head,
    //    additive halo sleeve, and a colored trail so it reads clearly as a
    //    fired energy weapon (and not a tiny stick).
    if (style === 'laser' || style === 'beam' || style === 'rail') {
      const length = style === 'rail' ? baseRadius * 14 : baseRadius * 10;
      const core = new THREE.Mesh(
        new THREE.CylinderGeometry(
          baseRadius * 0.55, baseRadius * 0.35, length, 10,
        ),
        new THREE.MeshBasicMaterial({ color: 0xffffff }),
      );
      core.geometry.rotateX(Math.PI / 2);
      group.add(core);

      const inner = new THREE.Mesh(
        new THREE.CylinderGeometry(
          baseRadius * 0.9, baseRadius * 0.55, length, 12,
        ),
        new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.75,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      inner.geometry.rotateX(Math.PI / 2);
      group.add(inner);

      const halo = new THREE.Mesh(
        new THREE.CylinderGeometry(
          baseRadius * 1.4, baseRadius * 0.8, length, 14,
        ),
        new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.35,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      halo.geometry.rotateX(Math.PI / 2);
      group.add(halo);

      const tip = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 1.25, 12, 12),
        new THREE.MeshBasicMaterial({
          color: 0xffffff, transparent: true, opacity: 0.95,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      tip.position.z = length * 0.5;
      group.add(tip);

      const tipHalo = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 2.0, 14, 14),
        new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.45,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      tipHalo.position.z = length * 0.5;
      group.add(tipHalo);

      const trail = this.createEngineTrail(color, 35, baseRadius * 0.6);
      trail.position.set(0, 0, -length * 0.5);
      group.add(trail);
      return { group, entityType: entity.type, trail };
    }

    // ── Missile / torpedo / swarm: cone body with exhaust flame.
    if (style === 'missile' || style === 'torpedo' || style === 'swarm') {
      const bodyLen = style === 'torpedo' ? baseRadius * 7 : baseRadius * 5;
      const body = new THREE.Mesh(
        new THREE.ConeGeometry(baseRadius * 1.1, bodyLen, 10),
        new THREE.MeshStandardMaterial({
          color, emissive: color, emissiveIntensity: 0.6,
          metalness: 0.4, roughness: 0.5,
        }),
      );
      body.geometry.rotateX(-Math.PI / 2);
      body.geometry.translate(0, 0, bodyLen * 0.25);
      group.add(body);

      const tail = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 1.2, 10, 10),
        new THREE.MeshBasicMaterial({
          color: 0xffaa55, transparent: true, opacity: 0.85,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      tail.position.z = -bodyLen * 0.4;
      group.add(tail);

      const trail = this.createEngineTrail(0xffaa55, 60, baseRadius * 0.8);
      trail.position.set(0, 0, -bodyLen * 0.4);
      group.add(trail);
      return { group, entityType: entity.type, trail };
    }

    // ── Plasma / antimatter / nova: layered sphere with pulsing halo ring.
    if (style === 'plasma' || style === 'antimatter' || style === 'nova' || style === 'arc') {
      const core = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 1.4, 14, 14),
        new THREE.MeshBasicMaterial({ color }),
      );
      group.add(core);
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 2.6, 14, 14),
        new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.4,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      group.add(halo);
      const ringGeo = new THREE.RingGeometry(baseRadius * 1.8, baseRadius * 2.6, 24);
      const ring = new THREE.Mesh(
        ringGeo,
        new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.55,
          side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      group.add(ring);

      const trail = this.createEngineTrail(color, 45, baseRadius * 0.8);
      trail.position.set(0, 0, -baseRadius * 1.5);
      group.add(trail);
      return { group, entityType: entity.type, trail, spinnable: halo };
    }

    // ── Prism / quantum / crystal: spinning octahedron.
    if (style === 'prism' || style === 'quantum' || style === 'crystal') {
      const core = new THREE.Mesh(
        new THREE.OctahedronGeometry(baseRadius * 1.5, 0),
        new THREE.MeshStandardMaterial({
          color, emissive: color, emissiveIntensity: 0.9,
          metalness: 0.8, roughness: 0.15, flatShading: true,
        }),
      );
      group.add(core);
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 2.5, 10, 10),
        new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.25,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      group.add(halo);

      const trail = this.createEngineTrail(color, 35, baseRadius * 0.7);
      trail.position.set(0, 0, -baseRadius * 1.5);
      group.add(trail);
      return { group, entityType: entity.type, trail, spinnable: core };
    }

    // ── Void / singularity / disruptor: dark core with bright accent ring.
    if (style === 'void' || style === 'singularity' || style === 'disruptor') {
      const core = new THREE.Mesh(
        new THREE.IcosahedronGeometry(baseRadius * 1.5, 0),
        new THREE.MeshBasicMaterial({ color: 0x110022 }),
      );
      group.add(core);
      const ringGeo = new THREE.RingGeometry(baseRadius * 1.7, baseRadius * 2.4, 24);
      const ring = new THREE.Mesh(
        ringGeo,
        new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.7, side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      group.add(ring);

      const trail = this.createEngineTrail(color, 40, baseRadius * 0.7);
      trail.position.set(0, 0, -baseRadius * 1.5);
      group.add(trail);
      return { group, entityType: entity.type, trail, spinnable: ring };
    }

    // ── Needler / coil: slim capsule with sparkle trail.
    if (style === 'needler' || style === 'coil') {
      const length = baseRadius * 7;
      const body = new THREE.Mesh(
        new THREE.CylinderGeometry(baseRadius * 0.4, baseRadius * 0.2, length, 6),
        new THREE.MeshStandardMaterial({
          color, emissive: color, emissiveIntensity: 0.7, metalness: 0.8, roughness: 0.2,
        }),
      );
      body.geometry.rotateX(Math.PI / 2);
      body.geometry.translate(0, 0, length * 0.25);
      group.add(body);

      const trail = this.createEngineTrail(color, 30, baseRadius * 0.4);
      trail.position.set(0, 0, -length * 0.2);
      group.add(trail);
      return { group, entityType: entity.type, trail };
    }

    // ── Chrono / ether: double-layered translucent sphere with sparkle.
    if (style === 'chrono' || style === 'ether') {
      const inner = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius, 10, 10),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9 }),
      );
      group.add(inner);
      const outer = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 2.2, 14, 14),
        new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.45,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      group.add(outer);

      const trail = this.createEngineTrail(color, 40, baseRadius * 0.7);
      trail.position.set(0, 0, -baseRadius * 1.5);
      group.add(trail);
      return { group, entityType: entity.type, trail, spinnable: outer };
    }

    // ── Gatling / shotgun / cluster: small fast pellet with brief trail.
    if (style === 'gatling' || style === 'shotgun' || style === 'cluster') {
      const core = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 0.9, 8, 8),
        new THREE.MeshBasicMaterial({ color }),
      );
      group.add(core);
      const glow = new THREE.Mesh(
        new THREE.SphereGeometry(baseRadius * 1.6, 8, 8),
        new THREE.MeshBasicMaterial({
          color, transparent: true, opacity: 0.3,
          blending: THREE.AdditiveBlending, depthWrite: false,
        }),
      );
      group.add(glow);
      const trail = this.createEngineTrail(color, 20, baseRadius * 0.4);
      trail.position.set(0, 0, -baseRadius * 1.2);
      group.add(trail);
      return { group, entityType: entity.type, trail };
    }

    // ── Fallback generic projectile (any unknown style): brighter than the
    //    basic blaster so it always reads as "better than default".
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(baseRadius * 1.3, 12, 12),
      new THREE.MeshBasicMaterial({ color }),
    );
    group.add(core);
    const glow = new THREE.Mesh(
      new THREE.SphereGeometry(baseRadius * 2.4, 12, 12),
      new THREE.MeshBasicMaterial({
        color, transparent: true, opacity: 0.45,
        blending: THREE.AdditiveBlending, depthWrite: false,
      }),
    );
    group.add(glow);
    const trail = this.createEngineTrail(color, 40, baseRadius * 0.7);
    trail.position.set(0, 0, -baseRadius * 1.5);
    group.add(trail);
    void trailStyle;
    return { group, entityType: entity.type, trail, spinnable: core };
  }

  private createPlanetVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();

    const radius = (entity.extra['radius'] as number) ?? 50;
    const color = (entity.extra['color'] as number) ?? 0x4488aa;

    const geometry = new THREE.SphereGeometry(radius, 32, 32);
    const material = new THREE.MeshStandardMaterial({
      color,
      metalness: 0.1,
      roughness: 0.9,
    });
    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);

    const atmosGeo = new THREE.SphereGeometry(radius * 1.02, 32, 32);
    const atmosMat = new THREE.MeshBasicMaterial({
      color: 0x88bbff,
      transparent: true,
      opacity: 0.15,
      side: THREE.FrontSide,
    });
    const atmosphere = new THREE.Mesh(atmosGeo, atmosMat);
    group.add(atmosphere);

    return { group, entityType: entity.type, atmosphere };
  }

  private createStarVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();

    const radius = (entity.extra['radius'] as number) ?? 200;
    const color = (entity.extra['color'] as number) ?? 0xffdd44;

    const geometry = new THREE.SphereGeometry(radius, 32, 32);
    const material = new THREE.MeshBasicMaterial({ color });
    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);

    // Corona collision boundary visualization
    const coronaGeo = new THREE.SphereGeometry(radius * 3, 24, 24);
    const coronaMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.04,
      side: THREE.BackSide,
      depthWrite: false,
    });
    const corona = new THREE.Mesh(coronaGeo, coronaMat);
    group.add(corona);

    const spriteMat = new THREE.SpriteMaterial({
      map: createGlowTexture(),
      color,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const glowSprite = new THREE.Sprite(spriteMat);
    glowSprite.scale.setScalar(radius * 4);
    group.add(glowSprite);

    return { group, entityType: entity.type, glowSprite };
  }

  private createStationVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();

    const ownerRace = entity.extra['ownerRace'] as number | null | undefined;
    const raceInfo =
      ownerRace != null && ownerRace !== undefined && RACE_INFO[ownerRace as RaceId]
        ? RACE_INFO[ownerRace as RaceId]
        : null;
    const primary = raceInfo?.primaryColor ?? 0x88aacc;
    const secondary = raceInfo?.secondaryColor ?? 0x00ff88;
    const dimPrimary = (primary & 0xfefefe) >> 1;

    // Stations were noticeably small for how important they are as
    // map landmarks (and how far away players approach them from).
    // Bumping the silhouette ~50% larger plus adding a low-cost
    // PointLight + brighter beacons turns each station into a
    // recognisable "lighthouse" you can spot from across the
    // sector without inflating its `STATION_SAFE_RADIUS` (still
    // 200u — server-authoritative, see shared/game.ts).
    const STATION_SCALE = 3.0;

    const coreMat = new THREE.MeshStandardMaterial({
      color: primary,
      emissive: dimPrimary,
      emissiveIntensity: 0.6,
      metalness: 0.9,
      roughness: 0.15,
      flatShading: true,
    });
    const armMat = new THREE.MeshStandardMaterial({
      color: (primary & 0xfefefe) >> 1,
      emissive: dimPrimary,
      emissiveIntensity: 0.35,
      metalness: 0.8,
      roughness: 0.25,
    });
    const accentMat = new THREE.MeshStandardMaterial({
      color: secondary,
      emissive: secondary,
      emissiveIntensity: 1.0,
      metalness: 0.5,
      roughness: 0.3,
    });
    // Beacon material is *separate* from the static accent so the
    // update tick can pulse only the docking lights, not the inner
    // ring (which would look weird strobing in lockstep). Stored on
    // the visual via `beaconMaterials` so the dispose path can
    // release it like the rest.
    const beaconMat = new THREE.MeshStandardMaterial({
      color: secondary,
      emissive: secondary,
      emissiveIntensity: 1.6,
      metalness: 0.4,
      roughness: 0.2,
    });

    // Central hub
    const octaGeo = new THREE.OctahedronGeometry(12 * STATION_SCALE, 1);
    const octa = new THREE.Mesh(octaGeo, coreMat);
    group.add(octa);

    // Wireframe on hub
    const wireGeo = new THREE.WireframeGeometry(octaGeo);
    const wireMat = new THREE.LineBasicMaterial({
      color: secondary,
      transparent: true,
      opacity: 0.4,
    });
    const wire = new THREE.LineSegments(wireGeo, wireMat);
    group.add(wire);

    // Structural arms (3 axes → 6 visible directions because each
    // cylinder runs through the hub origin).
    const cylGeo = new THREE.CylinderGeometry(4.4, 4.4, 90, 8);
    const armDirs = [
      { rot: [0, 0, Math.PI / 2] },
      { rot: [Math.PI / 2, 0, 0] },
      { rot: [0, 0, 0] },
    ];
    for (const dir of armDirs) {
      const arm = new THREE.Mesh(cylGeo, armMat);
      arm.rotation.set(dir.rot[0], dir.rot[1], dir.rot[2]);
      group.add(arm);
    }

    // Outer docking ring
    const ringGeo = new THREE.TorusGeometry(22 * STATION_SCALE, 2.8, 16, 48);
    const ring = new THREE.Mesh(ringGeo, armMat);
    ring.rotation.x = Math.PI / 2;
    group.add(ring);

    // Inner accent ring
    const innerRingGeo = new THREE.TorusGeometry(16 * STATION_SCALE, 1.2, 12, 48);
    const innerRing = new THREE.Mesh(innerRingGeo, accentMat);
    innerRing.rotation.x = Math.PI / 2;
    group.add(innerRing);

    // Antenna spires
    const spireGeo = new THREE.ConeGeometry(2.4, 28, 6);
    const spire1 = new THREE.Mesh(spireGeo, coreMat);
    spire1.position.set(0, 18 * STATION_SCALE, 0);
    group.add(spire1);
    const spire2 = new THREE.Mesh(spireGeo, coreMat);
    spire2.position.set(0, -18 * STATION_SCALE, 0);
    spire2.rotation.x = Math.PI;
    group.add(spire2);

    // Docking bay beacons — bumped count + size so the docking ring
    // reads as "inhabited" from far away. Sharing a single beaconMat
    // means they all pulse together when the update tick re-tints
    // its `emissiveIntensity`.
    const beaconGeo = new THREE.SphereGeometry(2.0, 10, 10);
    const beaconRingRadius = 22 * STATION_SCALE;
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const beacon = new THREE.Mesh(beaconGeo, beaconMat);
      beacon.position.set(
        Math.cos(angle) * beaconRingRadius,
        0,
        Math.sin(angle) * beaconRingRadius,
      );
      group.add(beacon);
    }
    // Spire-tip warning lights so the vertical extent is easy to
    // pick out against the starfield.
    const tipBeaconGeo = new THREE.SphereGeometry(2.8, 12, 12);
    const tipTop = new THREE.Mesh(tipBeaconGeo, beaconMat);
    tipTop.position.set(0, 18 * STATION_SCALE + 14, 0);
    group.add(tipTop);
    const tipBot = new THREE.Mesh(tipBeaconGeo, beaconMat);
    tipBot.position.set(0, -18 * STATION_SCALE - 14, 0);
    group.add(tipBot);

    // Real point light — gives nearby asteroids / ships a soft
    // station-coloured wash so the scene reads like the station is
    // actually projecting light, not just self-glowing. Range is
    // bounded so multiple stations across loaded sectors don't
    // overlap into a uniform haze. Colour mixes primary + secondary
    // so racially branded stations still look distinct.
    const stationLight = new THREE.PointLight(secondary, 1.8, 640, 1.5);
    stationLight.position.set(0, 0, 0);
    group.add(stationLight);

    // Glow sprite — bigger so it bleeds well past the docking ring
    // and the station registers as a "blip of light" even at LOD
    // distances where the meshes shrink to a few pixels.
    const glowMat = new THREE.SpriteMaterial({
      map: createGlowTexture(),
      color: secondary,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.4,
    });
    const glowSprite = new THREE.Sprite(glowMat);
    glowSprite.scale.setScalar(280);
    group.add(glowSprite);

    // The inner ring spins (kept for backwards-compat with the
    // `spinnable` field; the rest of the structure is now too big
    // to spin convincingly without making the silhouette wobble).
    const spinnable = innerRing;

    return {
      group,
      entityType: entity.type,
      spinnable,
      glowSprite,
      beaconMaterials: [beaconMat],
      pointLight: stationLight,
      pulsePhase: Math.random() * Math.PI * 2,
    };
  }

  private createWarpGateVisual(_entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();

    // Warp gates were notoriously hard to spot in deep / dark
    // sectors at the previous radius (8u), especially with the
    // background nebula colours often falling close to the gate's
    // purple. We bump the silhouette ~75% larger, add a brighter
    // self-glow, a halo sprite, an actual point light, and a
    // perpendicular accent ring — it's a navigation landmark, not
    // décor, so legibility wins over subtlety.
    //
    // Note: server-side `WARP_GATE_DISTANCE` (the proximity needed
    // to USE the gate) is unchanged at 30u so the new visual still
    // sits comfortably inside the use-radius bubble. Pilots get a
    // bigger target without changing where the actual interaction
    // triggers.
    const GATE_RADIUS = 28;
    const RING_TUBE = 3.2;
    const PRIMARY_COLOR = 0x7a5cff;
    const ACCENT_COLOR = 0xb098ff;
    const PORTAL_COLOR = 0x4a2cb8;

    // Main rotating ring
    const torusGeo = new THREE.TorusGeometry(GATE_RADIUS, RING_TUBE, 16, 48);
    const torusMat = new THREE.MeshStandardMaterial({
      color: PRIMARY_COLOR,
      metalness: 0.9,
      roughness: 0.1,
      emissive: 0x4a2cd8,
      emissiveIntensity: 1.0,
    });
    const spinnable = new THREE.Mesh(torusGeo, torusMat);
    group.add(spinnable);

    // Outer additive halo — wider than the ring tube so the gate
    // reads as "lit" rather than just "outlined".
    const glowGeo = new THREE.TorusGeometry(GATE_RADIUS, 8.0, 8, 48);
    const glowMat = new THREE.MeshBasicMaterial({
      color: ACCENT_COLOR,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    group.add(glow);

    // Perpendicular accent ring — gives the silhouette a 3D read at
    // any approach angle. Without it the gate vanishes when viewed
    // edge-on (zero pixel width).
    const accentGeo = new THREE.TorusGeometry(GATE_RADIUS, 1.6, 8, 48);
    const accentMat = new THREE.MeshStandardMaterial({
      color: ACCENT_COLOR,
      emissive: ACCENT_COLOR,
      emissiveIntensity: 1.4,
      metalness: 0.4,
      roughness: 0.3,
    });
    const accentRing = new THREE.Mesh(accentGeo, accentMat);
    accentRing.rotation.y = Math.PI / 2;
    group.add(accentRing);

    // Portal disc — inner "swirling" surface. Brighter and slightly
    // larger relative to the ring than before so the gate feels
    // active even when stationary.
    const portalGeo = new THREE.CircleGeometry(GATE_RADIUS - 1, 48);
    const portalMat = new THREE.MeshBasicMaterial({
      color: PORTAL_COLOR,
      transparent: true,
      opacity: 0.75,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const portal = new THREE.Mesh(portalGeo, portalMat);
    group.add(portal);

    // Real point light so the gate genuinely illuminates nearby
    // ships / asteroids in the dark — reinforces the "you found it"
    // moment when a pilot warps near one. Range capped at 260u so a
    // cluster of gates in adjacent sectors doesn't overcompound.
    const gateLight = new THREE.PointLight(ACCENT_COLOR, 2.2, 520, 1.5);
    group.add(gateLight);

    // Halo sprite for long-distance readability — by the time the
    // ring mesh is too small to see, the additive sprite still
    // shows up as a coloured dot against the starfield.
    const haloMat = new THREE.SpriteMaterial({
      map: createGlowTexture(),
      color: ACCENT_COLOR,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.6,
    });
    const halo = new THREE.Sprite(haloMat);
    halo.scale.setScalar(240);
    group.add(halo);

    return {
      group,
      entityType: _entity.type,
      spinnable,
      glowSprite: halo,
      beaconMaterials: [accentMat],
      pointLight: gateLight,
      pulsePhase: Math.random() * Math.PI * 2,
    };
  }

  private createCometVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();

    const geometry = new THREE.SphereGeometry(1.5, 12, 12);
    const material = new THREE.MeshStandardMaterial({
      color: 0xaaccff,
      emissive: 0x4466aa,
      emissiveIntensity: 0.3,
    });
    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);

    const particleTail = this.createCometTail();
    group.add(particleTail);

    return { group, entityType: entity.type, particleTail };
  }

  private createResourceDropVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();

    const tier = entity.resourceTier;
    const resourceInfo = tier !== undefined
      ? (RESOURCE_INFO as Record<string, { color?: number }>)[String(tier)]
      : undefined;
    const color = resourceInfo?.color ?? RESOURCE_COLORS[String(tier) ?? ''] ?? 0x00ffaa;

    const gemGeo = new THREE.OctahedronGeometry(2, 0);
    const gemMat = new THREE.MeshStandardMaterial({
      color,
      metalness: 0.9,
      roughness: 0.1,
      emissive: color,
      emissiveIntensity: 0.7,
      flatShading: true,
    });
    const spinnable = new THREE.Mesh(gemGeo, gemMat);
    group.add(spinnable);

    const wireGeo = new THREE.WireframeGeometry(gemGeo);
    const wireMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.4,
    });
    const wireframe = new THREE.LineSegments(wireGeo, wireMat);
    group.add(wireframe);

    const glowGeo = new THREE.SphereGeometry(3.5, 12, 12);
    const glowMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.15,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    group.add(glow);

    const spriteMat = new THREE.SpriteMaterial({
      map: createGlowTexture(),
      color,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.5,
    });
    const glowSprite = new THREE.Sprite(spriteMat);
    glowSprite.scale.setScalar(8);
    group.add(glowSprite);

    return { group, entityType: entity.type, spinnable, wireframe, glowSprite };
  }

  private createDefaultVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();
    const geometry = new THREE.BoxGeometry(1, 1, 1);
    const material = new THREE.MeshStandardMaterial({ color: 0xff00ff });
    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);
    return { group, entityType: entity.type };
  }

  /**
   * Battlezone-style ground building. Built from Lego-block primitives
   * (boxes + cylinders + cones) so every base reads as a low-poly
   * polygonal silhouette against the heightmap, not a floating orbital
   * station. Picks a layout based on `_extra.kind`:
   *
   *   - **Spaceport** — wide circular landing pad with a control
   *     tower and floodlight pylons. Reads as the safe "home base".
   *   - **Outpost** — small bunker with a single rotating sensor mast.
   *   - **Fortress** — heavy citadel with crenellated walls and four
   *     corner cannon turrets.
   *   - **Refinery** — stacked silo cluster with smokestacks emitting
   *     a slow plume.
   *
   * Coloured to the owning race so players can tell faction allegiance
   * at a glance the same way they do for orbital stations. Static — no
   * physics, no spin, no per-frame work beyond the beacon pulse.
   */
  private createSurfaceBaseVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();
    const kind = (entity.extra['kind'] as number | undefined) ?? 0;
    const radius = (entity.extra['radius'] as number | undefined) ?? 24;
    const ownerRace = entity.extra['ownerRace'] as number | undefined;
    const raceInfo =
      ownerRace !== undefined && RACE_INFO[ownerRace as RaceId]
        ? RACE_INFO[ownerRace as RaceId]
        : null;
    const primary = raceInfo?.primaryColor ?? 0x6688aa;
    const accent = raceInfo?.secondaryColor ?? 0x44ddff;
    const dim = (primary & 0xfefefe) >> 1;

    const hullMat = new THREE.MeshStandardMaterial({
      color: primary,
      emissive: dim,
      emissiveIntensity: 0.18,
      metalness: 0.55,
      roughness: 0.55,
      flatShading: true,
    });
    const accentMat = new THREE.MeshStandardMaterial({
      color: accent,
      emissive: accent,
      emissiveIntensity: 1.1,
      metalness: 0.4,
      roughness: 0.3,
      flatShading: true,
    });
    const beaconMat = new THREE.MeshStandardMaterial({
      color: accent,
      emissive: accent,
      emissiveIntensity: 1.6,
      metalness: 0.3,
      roughness: 0.25,
    });

    // Common foundation slab so every building "sits on the ground"
    // visually instead of being a free-floating tower. Diameter scales
    // with the base's radius so spaceports get a much wider apron than
    // outposts. Slightly inset (`radius * 0.95`) so the rim never
    // pokes through the heightmap on a slope.
    const slab = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * 0.95, radius * 1.0, 2, 24),
      hullMat,
    );
    slab.position.y = 1; // half the slab thickness so the bottom face is at y=0
    group.add(slab);

    const beaconMaterials: THREE.MeshStandardMaterial[] = [beaconMat];

    switch (kind) {
      case 3: {
        // Spaceport: circular landing pad with a low control tower and
        // four floodlight pylons. Pad diameter is the full radius so
        // the player can spot it from across the playable square.
        // Glowing inner disc — this is the "press P to LIFTOFF"
        // pickup point. Sized to match the server-side liftoff
        // proximity gate (60 u radius) so the visual cue and the
        // game rule visually agree.
        const liftoffPad = new THREE.Mesh(
          new THREE.CircleGeometry(radius * 0.6, 32),
          new THREE.MeshStandardMaterial({
            color: accent,
            emissive: accent,
            emissiveIntensity: 0.85,
            metalness: 0.2,
            roughness: 0.4,
            transparent: true,
            opacity: 0.55,
            side: THREE.DoubleSide,
          }),
        );
        liftoffPad.rotation.x = -Math.PI / 2;
        liftoffPad.position.y = 2.15;
        group.add(liftoffPad);
        // Vertical light pillar so the launchpad is visible across
        // the map even when the camera is at terrain level. Uses
        // additive blending so it reads as a beam, not a wall.
        const beam = new THREE.Mesh(
          new THREE.CylinderGeometry(radius * 0.5, radius * 0.55, 60, 16, 1, true),
          new THREE.MeshBasicMaterial({
            color: accent,
            transparent: true,
            opacity: 0.18,
            side: THREE.DoubleSide,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
          }),
        );
        beam.position.y = 32;
        group.add(beam);
        const padRing = new THREE.Mesh(
          new THREE.TorusGeometry(radius * 0.7, 0.8, 6, 36),
          accentMat,
        );
        padRing.rotation.x = Math.PI / 2;
        padRing.position.y = 2.2;
        group.add(padRing);

        // Hex landing markers on the pad
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          const marker = new THREE.Mesh(
            new THREE.BoxGeometry(2.5, 0.5, 1.2),
            accentMat,
          );
          marker.position.set(Math.cos(a) * radius * 0.5, 2.3, Math.sin(a) * radius * 0.5);
          marker.rotation.y = -a;
          group.add(marker);
        }

        const tower = new THREE.Mesh(
          new THREE.CylinderGeometry(3, 4, 14, 8),
          hullMat,
        );
        tower.position.set(radius * 0.55, 9, radius * 0.55);
        group.add(tower);
        const towerCap = new THREE.Mesh(
          new THREE.OctahedronGeometry(3.2),
          accentMat,
        );
        towerCap.position.set(radius * 0.55, 17, radius * 0.55);
        group.add(towerCap);

        for (let i = 0; i < 4; i++) {
          const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
          const pylon = new THREE.Mesh(
            new THREE.BoxGeometry(1.2, 8, 1.2),
            hullMat,
          );
          pylon.position.set(Math.cos(a) * radius * 0.85, 6, Math.sin(a) * radius * 0.85);
          group.add(pylon);
          const lamp = new THREE.Mesh(new THREE.SphereGeometry(1.2, 8, 8), beaconMat);
          lamp.position.set(Math.cos(a) * radius * 0.85, 10, Math.sin(a) * radius * 0.85);
          group.add(lamp);
        }
        break;
      }
      case 1: {
        // Fortress: square citadel + 4 corner cannon turrets.
        const wallH = 9;
        const wall = new THREE.Mesh(
          new THREE.BoxGeometry(radius * 1.5, wallH, radius * 1.5),
          hullMat,
        );
        wall.position.y = wallH / 2 + 1;
        group.add(wall);
        // Crenellated cap so the silhouette reads as fortified, not a plain box.
        for (let i = 0; i < 12; i++) {
          const merlon = new THREE.Mesh(
            new THREE.BoxGeometry(radius * 0.18, 1.4, radius * 0.18),
            hullMat,
          );
          const t = i / 12;
          const ang = t * Math.PI * 2;
          merlon.position.set(
            Math.cos(ang) * radius * 0.7,
            wallH + 1.7,
            Math.sin(ang) * radius * 0.7,
          );
          group.add(merlon);
        }
        // 4 corner turrets
        for (let i = 0; i < 4; i++) {
          const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
          const turret = new THREE.Group();
          turret.position.set(
            Math.cos(a) * radius * 0.78,
            wallH + 1,
            Math.sin(a) * radius * 0.78,
          );
          const dome = new THREE.Mesh(
            new THREE.SphereGeometry(2.5, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2),
            hullMat,
          );
          turret.add(dome);
          const cannon = new THREE.Mesh(
            new THREE.CylinderGeometry(0.5, 0.7, 6, 8),
            accentMat,
          );
          cannon.rotation.z = Math.PI / 2;
          cannon.position.set(3, 0.5, 0);
          cannon.rotation.y = -a + Math.PI / 2;
          turret.add(cannon);
          group.add(turret);
        }
        break;
      }
      case 2: {
        // Refinery: 3 stacked silos + a tall smokestack.
        for (let i = 0; i < 3; i++) {
          const a = (i / 3) * Math.PI * 2;
          const silo = new THREE.Mesh(
            new THREE.CylinderGeometry(4, 4.5, 14, 12),
            hullMat,
          );
          silo.position.set(Math.cos(a) * 7, 8, Math.sin(a) * 7);
          group.add(silo);
          const cap = new THREE.Mesh(new THREE.SphereGeometry(4, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), accentMat);
          cap.position.set(Math.cos(a) * 7, 15, Math.sin(a) * 7);
          group.add(cap);
        }
        const stack = new THREE.Mesh(
          new THREE.CylinderGeometry(1.6, 2.2, 22, 10),
          hullMat,
        );
        stack.position.set(0, 12, 0);
        group.add(stack);
        const stackTop = new THREE.Mesh(new THREE.TorusGeometry(2.2, 0.5, 6, 18), beaconMat);
        stackTop.rotation.x = Math.PI / 2;
        stackTop.position.set(0, 23, 0);
        group.add(stackTop);
        break;
      }
      case 0:
      default: {
        // Outpost: bunker with a sensor mast. Read as a small command
        // post — most numerous building type so we keep the silhouette
        // tight.
        const bunker = new THREE.Mesh(
          new THREE.BoxGeometry(radius * 1.2, 5, radius * 1.2),
          hullMat,
        );
        bunker.position.y = 3.5;
        group.add(bunker);
        const cap = new THREE.Mesh(new THREE.OctahedronGeometry(radius * 0.45), hullMat);
        cap.position.y = 7;
        group.add(cap);
        const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.6, 12, 6), hullMat);
        mast.position.y = 14;
        group.add(mast);
        const dish = new THREE.Mesh(
          new THREE.SphereGeometry(2, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2),
          accentMat,
        );
        dish.rotation.x = Math.PI;
        dish.position.y = 20;
        group.add(dish);
        break;
      }
    }

    // Race-coloured beacon on every base so they're spottable from
    // across the playable square at night. One beacon material shared
    // across instances per-base so the update tick can pulse it.
    const beacon = new THREE.Mesh(
      new THREE.SphereGeometry(1.5, 12, 8),
      beaconMat,
    );
    beacon.position.set(0, kind === 3 ? 18 : kind === 1 ? 12 : kind === 2 ? 25 : 22, 0);
    group.add(beacon);

    // Beacons are emissive-only — we deliberately do NOT add a
    // per-base PointLight. Three.js's default
    // MeshStandardMaterial shader uses a `MAX_POINT_LIGHTS` macro,
    // so adding 5 per-base lights forces every material in the
    // scene (terrain, hovertank, base, flora, ship hull) to be
    // recompiled the next time it renders. That recompile is the
    // canonical "freezes when I move" stutter — players panning the
    // camera reveal a previously-culled material that now needs to
    // recompile against the new light count, dropping a frame on
    // the GPU thread. The emissive ring + beam already gives bases
    // a strong night silhouette and the surface HemisphereLight
    // illuminates them at distance; an extra dynamic light here
    // earns very little visual gain and costs every other shader
    // in the scene a recompile.
    return {
      group,
      entityType: entity.type,
      beaconMaterials,
      pulsePhase: Math.random() * Math.PI * 2,
    };
  }

  /**
   * Battlezone-style hover tank for surface NPCs. Squat hex hull on a
   * pair of glowing hover skids with a single-barrel turret on top —
   * keeps the silhouette unambiguously "ground vehicle" so the player
   * never confuses a surface patroller for an air interceptor.
   *
   * Race-coloured exactly like the orbital NPCs so allied vs hostile
   * reads from the existing colour palette work without any extra
   * tagging. Scales with NPC level just like flying ships do.
   */
  private createSurfaceTankVisual(entity: ClientEntity): EntityVisual {
    const group = new THREE.Group();
    const npcLevel = (entity.extra['npcLevel'] as number) ?? 1;
    const scale = 1.0 + Math.min(npcLevel - 1, 12) * 0.05;

    const raceId = entity.extra['raceId'] as number | undefined;
    const raceInfo =
      raceId !== undefined ? (RACE_INFO as Record<number, { primaryColor: number; secondaryColor: number }>)[raceId] : undefined;
    const fallback = DISPOSITION_COLORS[entity.disposition ?? 'neutral'] ?? 0xffaa22;
    const primary = raceInfo?.primaryColor ?? fallback;
    const accent = raceInfo?.secondaryColor ?? primary;
    const dim = (primary & 0xfefefe) >> 1;

    const hullMat = new THREE.MeshStandardMaterial({
      color: primary,
      emissive: dim,
      emissiveIntensity: 0.2,
      metalness: 0.55,
      roughness: 0.5,
      flatShading: true,
    });
    const accentMat = new THREE.MeshStandardMaterial({
      color: accent,
      emissive: accent,
      emissiveIntensity: 1.1,
      metalness: 0.4,
      roughness: 0.3,
      flatShading: true,
    });
    const thrusterMat = new THREE.MeshStandardMaterial({
      color: accent,
      emissive: accent,
      emissiveIntensity: 2.0,
      metalness: 0.0,
      roughness: 0.6,
      transparent: true,
      opacity: 0.9,
    });

    // Hex-prism main hull
    const hull = new THREE.Mesh(new THREE.CylinderGeometry(2.4, 2.4, 1.6, 6), hullMat);
    hull.rotation.y = Math.PI / 6;
    hull.position.y = 0.5;
    group.add(hull);

    // Two longitudinal hover skids glowing underneath
    for (const sx of [-1.5, 1.5]) {
      const skid = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.3, 4.5), thrusterMat);
      skid.position.set(sx, -0.45, 0);
      group.add(skid);
    }

    // Front sensor wedge so the player can read the tank's facing
    const wedge = new THREE.Mesh(new THREE.ConeGeometry(1.2, 1.6, 4), accentMat);
    wedge.rotation.x = Math.PI / 2;
    wedge.rotation.y = Math.PI / 4;
    wedge.position.set(0, 0.6, -2.0);
    group.add(wedge);

    // Turret + barrel on top
    const turret = new THREE.Group();
    turret.position.y = 1.5;
    const turretBody = new THREE.Mesh(new THREE.SphereGeometry(1.0, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), hullMat);
    turret.add(turretBody);
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, 3.2, 8), accentMat);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0.3, -1.6);
    turret.add(barrel);
    group.add(turret);

    // Subtle ground-glow under the hull so hover skids "read" as
    // anti-grav lift, not landing legs.
    const glowSprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: createGlowTexture(),
      color: accent,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.45,
    }));
    glowSprite.scale.set(8, 4, 1);
    glowSprite.position.y = -1.2;
    group.add(glowSprite);

    group.scale.setScalar(scale);

    return {
      group,
      entityType: entity.type,
      // Lie about the ship-class slug as 'fighter' so the existing
      // mid-life rebuild guard in `update()` doesn't try to swap us
      // out for a ship mesh on the next tick.
      shipClassSlug: 'surface-tank',
      glowSprite,
      pulsePhase: Math.random() * Math.PI * 2,
    };
  }

  private static readonly FOG_IMMUNE_TYPES = new Set([
    EntityType.Projectile,
  ]);

  private disableFogOnGroup(group: THREE.Group): void {
    group.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments || child instanceof THREE.Points || child instanceof THREE.Sprite) {
        const mat = child.material as Record<string, unknown>;
        if ('fog' in mat) mat['fog'] = false;
      }
    });
  }

  private updateVisual(visual: EntityVisual, entity: ClientEntity, dt: number): void {
    const { group } = visual;
    const pos = entity.transform.position;
    const rot = entity.transform.rotation;

    group.position.set(pos.x, pos.y, pos.z);
    // Surface bases sit flat on the ground. Surface tanks rotate
    // around their vertical axis only — no banking, no nose-up
    // pitching — so they read as ground vehicles. Both detected from
    // the server's `_extra.surfaceBase` / `_extra.surfaceNpc` flags
    // (already cached on `entity.extra`).
    const isGroundUnit = entity.extra['surfaceNpc'] === true;
    const isStaticBuilding = entity.extra['surfaceBase'] === true;
    if (isStaticBuilding) {
      group.rotation.set(0, 0, 0);
    } else if (isGroundUnit) {
      group.rotation.set(0, rot.y, 0, 'YXZ');
    } else {
      group.rotation.set(rot.x, rot.y, rot.z, 'YXZ');
    }

    if (entity.type === EntityType.Ship || entity.type === EntityType.NPC) {
      this.syncShipEquipment(visual, entity);
    }

    if (visual.trail) {
      this.updateTrailParticles(visual.trail, dt);
    }

    if (visual.particleTail) {
      this.updateTrailParticles(visual.particleTail, dt);
    }

    if (visual.spinnable) {
      visual.spinnable.rotation.y += dt * 1.5;
      visual.spinnable.rotation.x += dt * 0.7;
    }

    if (visual.thrusterCores && visual.thrusterCores.length > 0) {
      // Breathing engine glow. Each ship gets its own phase offset so
      // adjacent ships don't pulse in lockstep.
      const phase = visual.pulsePhase ?? 0;
      const t = performance.now() * 0.0045 + phase;
      const pulse = 1.4 + Math.sin(t) * 0.6;
      for (const mat of visual.thrusterCores) {
        mat.emissiveIntensity = pulse;
      }
    }

    if (visual.beaconMaterials && visual.beaconMaterials.length > 0) {
      // Slow lighthouse pulse for stations + warp gates. Phase
      // offset keeps adjacent landmarks out of sync so a sector
      // with two stations doesn't strobe as a single unit. Floor
      // at 0.7 so beacons never go fully dark — they're navigation
      // markers, not chase lights.
      const phase = visual.pulsePhase ?? 0;
      const t = performance.now() * 0.0018 + phase;
      const pulse = 1.4 + Math.sin(t) * 0.7;
      const intensity = Math.max(0.7, pulse);
      for (const mat of visual.beaconMaterials) {
        mat.emissiveIntensity = intensity;
      }
      if (visual.pointLight) {
        // Mirror the beacon pulse on the actual light so nearby
        // surfaces brighten / dim in lockstep — sells the
        // "lighthouse" read at any range.
        const baseIntensity = visual.pointLight.userData['baseIntensity'] as number
          ?? visual.pointLight.intensity;
        if (!visual.pointLight.userData['baseIntensity']) {
          visual.pointLight.userData['baseIntensity'] = baseIntensity;
        }
        visual.pointLight.intensity = baseIntensity * (0.8 + Math.sin(t) * 0.25);
      }
    }

    if (visual.glowSprite) {
      const baseScale = visual.glowSprite.userData['baseScale'] as number
        ?? visual.glowSprite.scale.x;
      if (!visual.glowSprite.userData['baseScale']) {
        visual.glowSprite.userData['baseScale'] = baseScale;
      }
      const pulse = 1 + Math.sin(performance.now() * 0.003) * 0.08;
      visual.glowSprite.scale.setScalar(baseScale * pulse);
    }

    if (visual.atmosphere) {
      (visual.atmosphere.material as THREE.MeshBasicMaterial).opacity =
        0.12 + Math.sin(performance.now() * 0.001) * 0.03;
    }

    if (visual.shieldBubble) {
      const flash = Math.max(0, visual.shieldFlash ?? 0);
      if (flash > 0) {
        const newFlash = Math.max(0, flash - dt);
        visual.shieldFlash = newFlash;
        const opacity = Math.min(0.55, newFlash * 1.2);
        (visual.shieldBubble.material as THREE.MeshBasicMaterial).opacity = opacity;
        visual.shieldBubble.visible = opacity > 0.01;
      } else {
        visual.shieldBubble.visible = false;
      }
    }
  }

  flashShieldOn(entityId: string, duration = 0.45): void {
    const visual = this.visuals.get(entityId);
    if (!visual) return;
    visual.shieldFlash = Math.max(visual.shieldFlash ?? 0, duration);
  }

  private syncShipEquipment(visual: EntityVisual, entity: ClientEntity): void {
    const scale = SHIP_CLASS_SCALES[resolveShipClassSlug(entity.shipClass)] ?? 2.5;

    const shieldColor = entity.extra['shieldVisualColor'] as number | undefined;
    const shieldStyle = entity.extra['shieldVisualStyle'] as string | undefined;

    if (shieldColor !== undefined && shieldStyle !== undefined) {
      if (visual.shieldBubbleColor !== shieldColor || visual.shieldBubbleStyle !== shieldStyle) {
        if (visual.shieldBubble) {
          visual.group.remove(visual.shieldBubble);
          visual.shieldBubble.geometry.dispose();
          (visual.shieldBubble.material as THREE.Material).dispose();
          visual.shieldBubble = undefined;
        }
        visual.shieldBubble = this.createShieldBubble(shieldColor, shieldStyle, scale);
        visual.group.add(visual.shieldBubble);
        visual.shieldBubbleColor = shieldColor;
        visual.shieldBubbleStyle = shieldStyle;
      }
    }

    const weaponStyle = entity.extra['weaponVisualStyle'] as string | undefined;
    const muzzleColor = entity.extra['weaponMuzzleColor'] as number | undefined;
    if (weaponStyle !== undefined && muzzleColor !== undefined) {
      if (visual.weaponMountStyle !== weaponStyle) {
        if (visual.weaponMount) {
          visual.group.remove(visual.weaponMount);
          visual.weaponMount.traverse((obj) => {
            if (obj instanceof THREE.Mesh) {
              obj.geometry.dispose();
              (obj.material as THREE.Material).dispose();
            }
          });
          visual.weaponMount = undefined;
        }
        visual.weaponMount = this.createWeaponMount(weaponStyle, muzzleColor, scale);
        visual.group.add(visual.weaponMount);
        visual.weaponMountStyle = weaponStyle;
      } else if (visual.weaponMount) {
        visual.weaponMount.traverse((obj) => {
          if (obj instanceof THREE.Mesh && (obj.material as any).color) {
            (obj.material as THREE.MeshBasicMaterial).color.set(muzzleColor);
          }
        });
      }
    }
  }

  private createShieldBubble(color: number, style: string, scale: number): THREE.Mesh {
    const radius = scale * 1.8;
    let geo: THREE.BufferGeometry;
    if (style === 'plating') {
      geo = new THREE.IcosahedronGeometry(radius * 0.95, 1);
    } else if (style === 'crystal') {
      geo = new THREE.OctahedronGeometry(radius, 1);
    } else if (style === 'quantum') {
      geo = new THREE.SphereGeometry(radius, 24, 16);
    } else {
      geo = new THREE.SphereGeometry(radius, 20, 14);
    }
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0,
      wireframe: style === 'quantum' || style === 'crystal',
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.visible = false;
    return mesh;
  }

  private createWeaponMount(style: string, color: number, scale: number): THREE.Group {
    const mount = new THREE.Group();
    const mat = new THREE.MeshBasicMaterial({ color });

    if (style === 'beam' || style === 'railgun') {
      const geo = new THREE.CylinderGeometry(0.12 * scale, 0.15 * scale, 1.4 * scale, 8);
      geo.rotateX(Math.PI / 2);
      const barrel = new THREE.Mesh(geo, mat);
      barrel.position.set(0, -0.1 * scale, -0.9 * scale);
      mount.add(barrel);
    } else if (style === 'missile') {
      for (let i = -1; i <= 1; i += 2) {
        const geo = new THREE.BoxGeometry(0.22 * scale, 0.22 * scale, 0.7 * scale);
        const pod = new THREE.Mesh(geo, mat);
        pod.position.set(i * 0.55 * scale, -0.1 * scale, -0.3 * scale);
        mount.add(pod);
      }
    } else if (style === 'prism') {
      const geo = new THREE.OctahedronGeometry(0.22 * scale, 0);
      const prism = new THREE.Mesh(geo, mat);
      prism.position.set(0, -0.1 * scale, -0.7 * scale);
      mount.add(prism);
    } else if (style === 'plasma') {
      const geo = new THREE.ConeGeometry(0.22 * scale, 0.8 * scale, 12);
      geo.rotateX(Math.PI / 2);
      const cone = new THREE.Mesh(geo, mat);
      cone.position.set(0, -0.1 * scale, -0.7 * scale);
      mount.add(cone);
    } else {
      const geo = new THREE.CylinderGeometry(0.08 * scale, 0.1 * scale, 0.9 * scale, 6);
      geo.rotateX(Math.PI / 2);
      const barrel = new THREE.Mesh(geo, mat);
      barrel.position.set(0, -0.1 * scale, -0.6 * scale);
      mount.add(barrel);
    }

    return mount;
  }

  private createEngineTrail(color: number, count = 60, size = 0.12): THREE.Points {
    const positions = new Float32Array(count * 3);
    const alphas = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 0.3;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 0.3;
      positions[i * 3 + 2] = -Math.random() * 3;
      alphas[i] = 1 - i / count;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('alpha', new THREE.BufferAttribute(alphas, 1));

    const material = new THREE.PointsMaterial({
      color,
      size,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });

    return new THREE.Points(geometry, material);
  }

  private createCometTail(): THREE.Points {
    const count = 200;
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const t = i / count;
      positions[i * 3] = (Math.random() - 0.5) * t * 3;
      positions[i * 3 + 1] = (Math.random() - 0.5) * t * 3;
      positions[i * 3 + 2] = -t * 15;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xaaddff,
      size: 0.15,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });

    return new THREE.Points(geometry, material);
  }

  private updateTrailParticles(trail: THREE.Points, dt: number): void {
    const positions = trail.geometry.attributes['position'] as THREE.BufferAttribute;
    if (!positions) return;
    const arr = positions.array as Float32Array;
    const count = positions.count;

    for (let i = count - 1; i > 0; i--) {
      arr[i * 3] = arr[(i - 1) * 3] + (Math.random() - 0.5) * 0.05;
      arr[i * 3 + 1] = arr[(i - 1) * 3 + 1] + (Math.random() - 0.5) * 0.05;
      arr[i * 3 + 2] = arr[(i - 1) * 3 + 2] - dt * 2;
    }

    arr[0] = (Math.random() - 0.5) * 0.1;
    arr[1] = (Math.random() - 0.5) * 0.1;
    arr[2] = 0;

    positions.needsUpdate = true;
  }

  removeAll(): void {
    for (const [id, visual] of this.visuals) {
      this.scene.removeEntity(id);
      this.disposeVisual(visual);
    }
    this.visuals.clear();
  }

  /**
   * Return the root Object3D used to render the given entity, or null if
   * the entity has no visual yet. Effects can use this to anchor or
   * follow a specific ship (e.g. level-up rings that need to track a
   * moving target).
   */
  getEntityObject3D(entityId: string): THREE.Object3D | null {
    return this.visuals.get(entityId)?.group ?? null;
  }

  private disposeVisual(visual: EntityVisual): void {
    visual.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          child.material.dispose();
        }
      } else if (child instanceof THREE.LineSegments) {
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      } else if (child instanceof THREE.Points) {
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      } else if (child instanceof THREE.Sprite) {
        (child.material as THREE.SpriteMaterial).dispose();
      }
    });
  }

  dispose(): void {
    this.removeAll();
  }
}

function randomizeVertices(geometry: THREE.BufferGeometry, amount: number): void {
  const positions = geometry.attributes['position'] as THREE.BufferAttribute;
  if (!positions) return;
  const arr = positions.array as Float32Array;
  for (let i = 0; i < arr.length; i++) {
    arr[i] += (Math.random() - 0.5) * amount * 2;
  }
  positions.needsUpdate = true;
  geometry.computeVertexNormals();
}

function createGlowTexture(): THREE.Texture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.2, 'rgba(255, 220, 100, 0.8)');
  gradient.addColorStop(0.5, 'rgba(255, 180, 50, 0.3)');
  gradient.addColorStop(1, 'rgba(255, 100, 0, 0)');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}
